import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TABLE_COLUMNS, FIELDS } from "@/lib/fields";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Download, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import CaseDrawer from "./CaseDrawer";
import CasesTable from "@/components/cases/CasesTable";
import CasesFilters from "@/components/cases/CasesFilters";

export default function Cases() {
  const { can, canView } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tat, setTat] = useState(searchParams.get("tat") || "");
  const [caseStatus, setCaseStatus] = useState("");
  const [feName, setFeName] = useState("");
  const [priority, setPriority] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeCase, setActiveCase] = useState(null);

  const columns = useMemo(() => TABLE_COLUMNS.filter((c) => canView(c)), [canView]);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (tat) params.tat = tat;
      if (caseStatus) params.case_status = caseStatus;
      if (feName) params.fe_name = feName;
      if (priority) params.priority_case = priority;
      if (Object.keys(colFilters).length) params.filters = JSON.stringify(colFilters);
      const r = await api.get("/cases", { params });
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, search, tat, caseStatus, feName, priority, colFilters]); // eslint-disable-line react-hooks/exhaustive-deps -- api/toast are stable imports

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setActiveCase(openId);
      setDrawerOpen(true);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps -- setters are stable

  const exportCsv = async () => {
    try {
      const params = { page: 1, limit: 5000 };
      if (search) params.search = search;
      if (tat) params.tat = tat;
      const r = await api.get("/cases", { params });
      const cols = FIELDS.filter((f) => canView(f.key));
      const rows = [cols.map((c) => c.label).join(",")];
      r.data.items.forEach((item) => {
        rows.push(cols.map((c) => {
          const v = item[c.key] ?? "";
          return `"${String(v).replace(/"/g, '""')}"`;
        }).join(","));
      });
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "cases_export.csv";
      a.click();
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteCase = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this case permanently?")) return;
    try {
      await api.delete(`/cases/${id}`);
      toast.success("Case deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteSelected = async () => {
    if (!window.confirm(`Delete ${selected.size} selected case(s) permanently?`)) return;
    try {
      const r = await api.post("/cases/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} case(s) deleted`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  const handleToggleAll = (v) => setSelected(v ? new Set(data.items.map((c) => c.id)) : new Set());
  const handleToggleRow = (id, v) => setSelected((prev) => {
    const n = new Set(prev);
    if (v) n.add(id); else n.delete(id);
    return n;
  });
  const handleColumnFilter = (col, val) => {
    setColFilters((prev) => {
      const next = { ...prev };
      if (val) next[col] = val; else delete next[col];
      return next;
    });
    setPage(1);
  };
  const handleOpenCase = (id) => { setActiveCase(id); setDrawerOpen(true); };

  return (
    <div className="space-y-4" data-testid="cases-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">Cases</h1>
          <p className="text-sm text-[#4B5563] mt-1">{data.total} {data.total === 1 ? "case" : "cases"} in the pipeline. Click a row to open the full record.</p>
        </div>
        <div className="flex gap-2">
          {can("delete_case") && selected.size > 0 && (
            <Button variant="outline" onClick={deleteSelected}
              className="rounded-sm border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/5" data-testid="delete-selected-button">
              <Trash2 className="h-4 w-4 mr-2" /> Delete Selected ({selected.size})
            </Button>
          )}
          <Button variant="outline" onClick={exportCsv} className="rounded-sm" data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          {can("create_case") && (
            <Button onClick={() => { setActiveCase(null); setDrawerOpen(true); }}
              className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-case-button">
              <Plus className="h-4 w-4 mr-2" /> Add Case
            </Button>
          )}
        </div>
      </div>

      <CasesFilters search={search} onSearchChange={setSearch} onSearchSubmit={() => { setPage(1); load(); }}
        tat={tat} onTatChange={(v) => { setTat(v); setPage(1); }}
        caseStatus={caseStatus} onCaseStatusChange={(v) => { setCaseStatus(v); setPage(1); }}
        priority={priority} onPriorityChange={(v) => { setPriority(v); setPage(1); }}
        feName={feName} onFeNameChange={setFeName} onFeBlur={() => { setPage(1); load(); }} />

      <CasesTable columns={columns} data={data} loading={loading} canDelete={can("delete_case")}
        selected={selected} onToggleAll={handleToggleAll} onToggleRow={handleToggleRow}
        colFilters={colFilters} onFilter={handleColumnFilter}
        onOpenCase={handleOpenCase} onDeleteCase={deleteCase} />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#4B5563]" data-testid="cases-pagination-info">Page {page} of {totalPages} · {data.total} {data.total === 1 ? "case" : "cases"}</span>
          <span className="text-xs text-[#4B5563] flex items-center gap-1.5" data-testid="duplicate-legend">
            <span className="inline-block w-3 h-3 bg-[#FBCFE8] border border-[#F9A8D4]"></span> Pink = duplicate Ref No. / Candidate / Address / Pincode / Contact
          </span>
          <span className="text-xs text-[#4B5563] flex items-center gap-1.5" data-testid="priority-legend">
            <span className="inline-block w-3 h-3 bg-[#D7E3BC] border border-[#A9C08B]"></span> Green = Priority case
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="pagination-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <CaseDrawer caseId={activeCase} open={drawerOpen} onOpenChange={setDrawerOpen} onSaved={load} />
    </div>
  );
}
